import moment from "moment-timezone";

/**
 * DST (Daylight Saving Time) utility functions for nomination handling.
 *
 * The portal timezone is read from the global config (window.config.consul.PORTAL_TIMEZONE).
 * If not configured, it defaults to "Europe/London".
 *
 * Key concepts:
 *  - A DST transition date is a day where the UTC offset changes between
 *    the start and end of the day (spring-forward loses an hour, fall-back gains one).
 *  - Nomination slots are half-hour periods. A normal day has 48 slots;
 *    a spring-forward day has 46 and a fall-back day has 50.
 *  - DST transition dates can only be nominated as a single day
 *    (start and end date must be the same).
 */

const DEFAULT_PORTAL_TIMEZONE = "Europe/London";

// ---------------------------------------------------------------------------
// Portal timezone
// ---------------------------------------------------------------------------

/**
 * Return the portal timezone from the global config, falling back to the default.
 * @returns {string} IANA timezone identifier (e.g. "Europe/London")
 */
export const getPortalTimezone = () => {
	if (
		typeof window !== "undefined" &&
		window.config &&
		window.config.consul &&
		window.config.consul.PORTAL_TIMEZONE
	) {
		return window.config.consul.PORTAL_TIMEZONE;
	}
	return DEFAULT_PORTAL_TIMEZONE;
};

// ---------------------------------------------------------------------------
// DST detection helpers
// ---------------------------------------------------------------------------

/**
 * Check whether a given date is a DST transition date in the portal timezone.
 * @param {moment.Moment} date - any moment representing the day to check
 * @returns {boolean}
 */
export const isDstTransitionDate = (date) => {
	if (!date) return false;
	const tz = getPortalTimezone();
	const dayStart = moment.tz(date.format("YYYY-MM-DD"), tz).startOf("day");
	const nextDayStart = dayStart.clone().add(1, "day");
	return dayStart.utcOffset() !== nextDayStart.utcOffset();
};

/**
 * Return the actual number of hours in a given day for the portal timezone.
 * Normal day -> 24, spring-forward -> 23, fall-back -> 25.
 * @param {moment.Moment} date
 * @returns {number}
 */
export const getHoursInDay = (date) => {
	const tz = getPortalTimezone();
	const dayStart = moment.tz(date.format("YYYY-MM-DD"), tz).startOf("day");
	const nextDayStart = dayStart.clone().add(1, "day");
	return nextDayStart.diff(dayStart, "hours");
};

/**
 * Return the number of half-hour nomination slots for a given date.
 *  - Normal day:     48
 *  - Spring-forward: 46
 *  - Fall-back:      50
 * @param {moment.Moment} date
 * @param {number}        [slotsPerHour=2]
 * @returns {number}
 */
export const getNominationSlotsForDate = (date, slotsPerHour = 2) => {
	return getHoursInDay(date) * slotsPerHour;
};

// ---------------------------------------------------------------------------
// Date-range validation
// ---------------------------------------------------------------------------

/**
 * Return true when there is at least one DST transition date strictly
 * between startDate and endDate (exclusive of both).
 * @param {moment.Moment} startDate
 * @param {moment.Moment} endDate
 * @returns {boolean}
 */
export const hasDstTransitionBetween = (startDate, endDate) => {
	if (!startDate || !endDate) return false;
	const current = startDate.clone().add(1, "day");
	while (current.isBefore(endDate, "day")) {
		if (isDstTransitionDate(current)) {
			return true;
		}
		current.add(1, "day");
	}
	return false;
};

// ---------------------------------------------------------------------------
// Timestamp generation
// ---------------------------------------------------------------------------

/**
 * Generate standard (non-DST) half-hour timestamps starting from the given time.
 * This is used as a fallback when no date is provided or the date is not
 * a DST transition day.
 * @param {number} slotCount - total number of slots (e.g. 48)
 * @param {string} startTime - "HH:mm" string for the first slot (e.g. "00:00")
 * @returns {string[]}
 */
const generateDefaultTimeStamps = (slotCount, startTime) => {
	const stamps = [];
	const parts = startTime.split(":");
	const offsetMinutes = (parseInt(parts[0], 10) * 60) + parseInt(parts[1], 10);
	const minutesPerSlot = (24 * 60) / slotCount;
	for (let i = 0; i < slotCount; i++) {
		const totalMinutes = (offsetMinutes + i * minutesPerSlot) % (24 * 60);
		const h = Math.floor(totalMinutes / 60);
		const m = Math.floor(totalMinutes % 60);
		stamps.push(`${h < 10 ? "0" : ""}${h}:${m < 10 ? "0" : ""}${m}`);
	}
	return stamps;
};

/**
 * Generate half-hour timestamps for a given date.
 *
 * When the date is null or is not a DST transition day, standard timestamps
 * are returned (48 by default).
 * For a spring-forward day the missing hour is skipped (46 stamps).
 * For a fall-back day the repeated hour appears twice, distinguished
 * with a " (1)" / " (2)" suffix (50 stamps).
 *
 * @param {moment.Moment|null} date
 * @param {number}             [slotsPerHour=2]
 * @param {string}             [startTime="00:00"] - "HH:mm" for the first slot
 * @returns {string[]}
 *   Each entry is a string like "01:00" or "01:00 (2)" for fall-back duplicates.
 */
export const generateDstAwareTimeStamps = (date, slotsPerHour = 2, startTime = "00:00") => {
	if (!/^\d{2}:\d{2}$/.test(startTime)) {
		console.error(`Invalid startTime format "${startTime}". Expected "HH:mm". Falling back to "00:00".`);
		startTime = "00:00";
	}
	// No date or not a DST day: return standard timestamps
	if (!date || !isDstTransitionDate(date)) {
		return generateDefaultTimeStamps(24 * slotsPerHour, startTime);
	}
	const tz = getPortalTimezone();
	const numSlots = getNominationSlotsForDate(date, slotsPerHour);
	const minutesPerSlot = 60 / slotsPerHour;
	const stamps = [];
	const seen = {};

	const parts = startTime.split(":");
	const startOffsetMinutes = (parseInt(parts[0], 10) * 60) + parseInt(parts[1], 10);
	const dayStart = moment.tz(date.format("YYYY-MM-DD"), tz)
		.startOf("day")
		.add(startOffsetMinutes, "minutes");

	for (let i = 0; i < numSlots; i++) {
		const slotTime = dayStart.clone().add(i * minutesPerSlot, "minutes");
		const formatted = slotTime.format("HH:mm");

		if (seen[formatted]) {
			// Fall-back: this HH:mm already appeared - mark both occurrences
			if (seen[formatted] === 1) {
				// Retroactively tag the first occurrence
				const firstIdx = stamps.findIndex((s) => s === formatted);
				if (firstIdx !== -1) {
					stamps[firstIdx] = `${formatted} (1)`;
				}
			}
			seen[formatted]++;
			stamps.push(`${formatted} (${seen[formatted]})`);
		} else {
			seen[formatted] = 1;
			stamps.push(formatted);
		}
	}

	return stamps;
};

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Return a human-readable information message about the DST status of a
 * date, or null if the date is a normal (non-transition) day.
 * @param {moment.Moment} date
 * @returns {string|null}
 */
export const getDstInfoMessage = (date) => {
	if (!date || !isDstTransitionDate(date)) return null;

	const hours = getHoursInDay(date);
	const slots = hours * 2;
	const tz = getPortalTimezone();

	if (hours < 24) {
		return (
			`DST spring-forward: This day has ${hours} hours (${slots} half-hour periods) ` +
			`in ${tz}. Clocks move forward - one hour is skipped.`
		);
	}
	return (
		`DST fall-back: This day has ${hours} hours (${slots} half-hour periods) ` +
		`in ${tz}. Clocks move back - one hour is repeated.`
	);
};

/**
 * Return a generic informational message explaining DST date-range
 * restrictions to the user.
 * @returns {string}
 */
export const getDstDateRangeRestrictionMessage = () =>
	"DST transition dates can only be nominated as a single day (start and end date must be the same). " +
	"When a transition date is selected the number of half-hour periods adjusts automatically " +
	"(46 for spring-forward, 50 for fall-back).";